import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Instagram, Facebook, Linkedin, FileText, Loader2, Zap, Sparkles,
  Send, Clock, Plus, Share2, FileEdit, Edit2, ExternalLink, X, CheckCircle
} from "lucide-react";
import BrandVisualHints from '../components/BrandVisualHints';

// ── Helper: Text kopieren ────────────────────────────────────────────────────
async function _copyText(text, onDone) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    onDone?.();
  } catch { /* silent */ }
}

// ── Amber-Box mit Copy-Button ─────────────────────────────────────────────────
const ImagePromptHintBox = ({ hint }) => {
  const [copied, setCopied] = useState(false);
  if (!hint) return null;

  const handleCopy = async () => {
    await _copyText(hint, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-start gap-2 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
      <span className="text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true">🖼️</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="text-xs font-medium text-amber-700">Bild- &amp; Thumbnail-Tipp</p>
          <button
            onClick={handleCopy}
            className={`text-xs px-2 py-0.5 rounded flex-shrink-0 transition-colors ${
              copied
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
            }`}
          >
            {copied ? "✓ Kopiert" : "Bild-Prompt kopieren"}
          </button>
        </div>
        <p className="text-xs text-amber-600 leading-snug">{hint}</p>
        <p className="text-xs text-amber-500 mt-1 leading-snug">
          Nutze diesen Tipp für KI-Bilder, Thumbnails oder als Briefing für Designer.
        </p>
      </div>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VariantEditor = ({ variant, onSave, onCancel }) => {
  const [hook, setHook] = useState(variant.hook);
  const [body, setBody] = useState(variant.body);
  const [cta, setCta] = useState(variant.cta);
  const [hashtagsStr, setHashtagsStr] = useState(variant.hashtags?.join(' ') || '');

  const handleSave = () => {
    onSave({
      hook,
      body,
      cta,
      hashtags: hashtagsStr.split(' ').filter(t => t.startsWith('#'))
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-stone-600">Hook</label>
        <input type="text" value={hook} onChange={(e) => setHook(e.target.value)} className="w-full p-2 text-sm border border-stone-200 rounded" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600">Body</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full p-2 text-sm border border-stone-200 rounded resize-none" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600">CTA</label>
        <input type="text" value={cta} onChange={(e) => setCta(e.target.value)} className="w-full p-2 text-sm border border-stone-200 rounded" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600">Hashtags (Leerzeichen-getrennt)</label>
        <input type="text" value={hashtagsStr} onChange={(e) => setHashtagsStr(e.target.value)} className="w-full p-2 text-sm border border-stone-200 rounded" />
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">Speichern</button>
        <button onClick={onCancel} className="px-3 py-2 bg-stone-100 text-sm rounded hover:bg-stone-200">Abbrechen</button>
      </div>
    </div>
  );
};

const MasterpostPage = () => {
  const [title, setTitle] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [hook, setHook] = useState('');
  const [body, setBody] = useState('');
  const [cta, setCta] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [baseHashtags, setBaseHashtags] = useState(['#Förderfuchs', '#AVGS', '#Coaching']);
  const [newHashtag, setNewHashtag] = useState('');
  const [selectedChannels, setSelectedChannels] = useState(['instagram', 'facebook', 'linkedin', 'google_business']);
  const [masterpost, setMasterpost] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  // FREIGABE-03 (16.08.2026): Diese Vorbelegung stand auf `true`. Zusammen mit dem
  // grünen Knopf darunter — der ohne jede Rückfrage sendet — genügte EIN Klick, um
  // ungeprüften KI-Text auf allen verbundenen echten Kanälen zu veröffentlichen.
  // Der Monatsplan-Weg fragt an derselben Stelle ausdrücklich nach
  // (PublishPreviewDialog). Eine nicht rückholbare Aktion nach außen ist nie der
  // Standardfall; wer sofort senden will, sagt es ausdrücklich.
  const [postImmediately, setPostImmediately] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [liveStatus, setLiveStatus] = useState(null);
  const [brandVisualHints, setBrandVisualHints] = useState(null);
  const [imagePromptHint, setImagePromptHint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [hookGenerating, setHookGenerating] = useState(false);
  const [audiences, setAudiences] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content-tools/pain-point/audiences`)
      .then(res => setAudiences(res.data.audiences || []))
      .catch(() => {});

    axios.get(`${API}/masterpost/live/status`)
      .then(res => setLiveStatus(res.data))
      .catch(() => {});
  }, []);

  const allChannels = [
    { key: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-pink-500' },
    { key: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
    { key: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700' },
    { key: 'google_business', name: 'Google Business', icon: () => <span className="text-sm font-bold">G</span>, color: 'bg-orange-500' },
  ];

  const toggleChannel = (channel) => {
    setSelectedChannels(prev =>
      prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
    );
  };

  const addHashtag = () => {
    if (newHashtag && !baseHashtags.includes(newHashtag)) {
      const tag = newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`;
      setBaseHashtags([...baseHashtags, tag]);
      setNewHashtag('');
    }
  };

  const generateHook = async () => {
    if (!targetAudience || !title) {
      toast.error('Bitte Thema und Zielgruppe ausfüllen');
      return;
    }
    setHookGenerating(true);
    try {
      const response = await axios.post(`${API}/content-tools/hook/generate`, {
        topic: title,
        target_audience: targetAudience,
        channel: selectedChannels[0] || 'instagram'
      });
      setHook(response.data.hook);
      toast.success('Hook generiert!');
    } catch {
      toast.error('Fehler beim Hook-Generieren');
    } finally {
      setHookGenerating(false);
    }
  };

  const createMasterpost = async () => {
    if (!title || !hook || !body || !cta) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    if (selectedChannels.length === 0) {
      toast.error('Bitte mindestens einen Kanal auswählen');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/masterpost/create`, {
        title, target_audience: targetAudience, hook, body, cta,
        media_url: mediaUrl || null, base_hashtags: baseHashtags, channels: selectedChannels
      });
      setMasterpost(response.data.masterpost);
      setBrandVisualHints(response.data.brand_visual_hints || null);
      setImagePromptHint(response.data.image_prompt_hint || null);
      toast.success('Masterpost mit Kanal-Varianten erstellt!');
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const updateVariant = async (channel, updates) => {
    if (!masterpost) return;
    try {
      await axios.put(`${API}/masterpost/${masterpost.id}/variant/${channel}/edit`, updates);
      setMasterpost(prev => ({
        ...prev,
        variants: prev.variants.map(v =>
          v.channel === channel ? { ...v, ...updates, is_edited: true } : v
        )
      }));
      setEditingVariant(null);
      toast.success(`${channel}-Variante aktualisiert`);
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const postMasterpost = async () => {
    if (!masterpost) return;
    const channelsToPost = selectedChannels.filter(c => masterpost.variants.some(v => v.channel === c));
    if (channelsToPost.length === 0) {
      toast.error('Keine Kanäle zum Posten ausgewählt');
      return;
    }
    // FREIGABE-03: Vor dem tatsächlichen Senden ausdrücklich nachfragen und dabei
    // benennen, WOHIN es geht. Ein Beitrag, der einmal draußen ist, lässt sich von
    // hier aus nicht zurückholen — auf der Plattform selbst muss er gelöscht werden.
    if (postImmediately) {
      const namen = channelsToPost
        .map((c) => allChannels.find((k) => k.key === c)?.name || c)
        .join(', ');
      const bestaetigt = window.confirm(
        `Diesen Beitrag jetzt wirklich veröffentlichen?\n\n` +
        `Ziel: ${namen}\n\n` +
        `Er erscheint sofort öffentlich. Zurücknehmen lässt er sich danach nur direkt ` +
        `auf der jeweiligen Plattform.`
      );
      if (!bestaetigt) return;
    }
    setPosting(true);
    try {
      const payload = { channels: channelsToPost, post_immediately: postImmediately };
      if (!postImmediately && scheduledDate && scheduledTime) {
        payload.scheduled_at = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      }
      const response = await axios.post(`${API}/masterpost/${masterpost.id}/post`, payload);
      const updatedMasterpost = await axios.get(`${API}/masterpost/${masterpost.id}`);
      setMasterpost(updatedMasterpost.data.masterpost);
      const summary = response.data.summary;
      toast.success(`${summary.posted} live gepostet, ${summary.scheduled} eingeplant, ${summary.simulated} simuliert`);
    } catch (error) {
      toast.error('Fehler beim Posten: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPosting(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setTargetAudience(''); setHook(''); setBody(''); setCta('');
    setMediaUrl(''); setBaseHashtags(['#Förderfuchs', '#AVGS', '#Coaching']);
    setMasterpost(null);
    setBrandVisualHints(null);
    setImagePromptHint(null);
    setSelectedChannels(['instagram', 'facebook', 'linkedin', 'google_business']);
  };

  const getChannelStatus = (channel) => {
    if (!liveStatus) return { is_live: false, status: 'loading' };
    return liveStatus.channels?.[channel] || { is_live: false, status: 'unknown' };
  };

  return (
    <div className="space-y-8" data-testid="masterpost-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">Masterpost</h1>
          <p className="text-stone-600 mt-1">Erstelle einen zentralen Post und generiere automatisch Kanal-Varianten</p>
        </div>
        {masterpost && (
          <button onClick={resetForm} className="btn-secondary flex items-center gap-2">
            <Plus size={18} />
            Neuer Masterpost
          </button>
        )}
      </div>

      {/* Live Status Banner */}
      {liveStatus && (
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={18} className="text-fox-fire" />
            <h3 className="font-semibold">Live-Posting Status</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {allChannels.map(channel => {
              const status = getChannelStatus(channel.key);
              const Icon = channel.icon;
              return (
                <div
                  key={channel.key}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    status.is_live
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  <Icon size={16} />
                  <span className="font-medium">{channel.name}</span>
                  <span className={`w-2 h-2 rounded-full ${status.is_live ? 'bg-green-500' : 'bg-amber-500'}`} />
                </div>
              );
            })}
          </div>
          {liveStatus.simulated_channels?.length > 0 && (
            <p className="text-xs text-stone-500 mt-2">
              ⚠️ {liveStatus.simulated_channels.join(', ')}: Nur Simulation (API-Keys nicht konfiguriert)
            </p>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Form */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
              <FileEdit size={20} className="text-fox-fire" />
              Masterpost erstellen
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Thema/Titel *</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. AVGS-Coaching für Arbeitssuchende" className="w-full p-3 border border-stone-200 rounded-lg" disabled={!!masterpost} />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Zielgruppe</label>
                <select value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} className="w-full p-3 border border-stone-200 rounded-lg" disabled={!!masterpost}>
                  <option value="">-- Zielgruppe wählen --</option>
                  {audiences.map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-stone-700">Hook *</label>
                  {!masterpost && (
                    <button onClick={generateHook} disabled={hookGenerating} className="text-xs px-2 py-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200 flex items-center gap-1">
                      {hookGenerating ? <Loader2 className="animate-spin" size={12} /> : <Zap size={12} />}
                      Auto-Hook
                    </button>
                  )}
                </div>
                <input type="text" value={hook} onChange={(e) => setHook(e.target.value)} placeholder="Aufmerksamkeits-Hook für die ersten 2-3 Sekunden" className="w-full p-3 border border-stone-200 rounded-lg" disabled={!!masterpost} />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Kernbotschaft *</label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Der Haupttext deines Posts (~150 Wörter)" rows={5} className="w-full p-3 border border-stone-200 rounded-lg resize-none" disabled={!!masterpost} />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Call-to-Action *</label>
                <input type="text" value={cta} onChange={(e) => setCta(e.target.value)} placeholder="z.B. Schreib uns für ein kostenloses Erstgespräch!" className="w-full p-3 border border-stone-200 rounded-lg" disabled={!!masterpost} />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Medien-URL (Bild/Video)</label>
                <input type="url" value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://example.com/bild.jpg" className="w-full p-3 border border-stone-200 rounded-lg" disabled={!!masterpost} />
                {mediaUrl && (
                  <div className="mt-2 p-2 bg-stone-50 rounded-lg">
                    <img src={mediaUrl} alt="Preview" className="w-32 h-32 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Basis-Hashtags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {baseHashtags.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-stone-100 text-stone-700 rounded-full text-sm flex items-center gap-1">
                      {tag}
                      {!masterpost && (
                        <button onClick={() => setBaseHashtags(baseHashtags.filter(t => t !== tag))} className="hover:text-red-500">
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {!masterpost && (
                  <div className="flex gap-2">
                    <input type="text" value={newHashtag} onChange={(e) => setNewHashtag(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addHashtag()} placeholder="#neuerHashtag" className="flex-1 p-2 border border-stone-200 rounded-lg text-sm" />
                    <button onClick={addHashtag} className="px-3 py-2 bg-stone-100 rounded-lg hover:bg-stone-200"><Plus size={16} /></button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Kanäle</label>
                <div className="flex flex-wrap gap-2">
                  {allChannels.map(channel => {
                    const Icon = channel.icon;
                    const isSelected = selectedChannels.includes(channel.key);
                    const status = getChannelStatus(channel.key);
                    return (
                      <button
                        key={channel.key}
                        onClick={() => !masterpost && toggleChannel(channel.key)}
                        disabled={!!masterpost}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                          isSelected ? `${channel.color} text-white` : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        <Icon size={16} />
                        {channel.name}
                        {!status.is_live && isSelected && <span className="text-xs opacity-75">(Sim)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!masterpost && (
                <button onClick={createMasterpost} disabled={loading || !title || !hook || !body || !cta} className="w-full btn-primary flex items-center justify-center gap-2">
                  {loading ? (
                    <><Loader2 className="animate-spin" size={18} /> Generiere Varianten...</>
                  ) : (
                    <><Sparkles size={18} /> Masterpost + Varianten erstellen</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right: Variants & Posting */}
        <div className="space-y-6">
          {masterpost && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Share2 size={20} className="text-purple-600" />
                Kanal-Varianten
                <span className="text-sm font-normal text-stone-500">({masterpost.variants?.length || 0})</span>
              </h2>

              <div className="space-y-4">
                {masterpost.variants?.map((variant) => {
                  const channel = allChannels.find(c => c.key === variant.channel);
                  const Icon = channel?.icon || FileText;
                  const isEditing = editingVariant === variant.channel;
                  const status = getChannelStatus(variant.channel);

                  return (
                    <div key={variant.channel} className="border border-stone-200 rounded-lg overflow-hidden">
                      <div className={`${channel?.color || 'bg-stone-500'} text-white px-4 py-2 flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <Icon size={18} />
                          <span className="font-medium">{channel?.name || variant.channel}</span>
                          {variant.is_edited && <span className="text-xs px-2 py-0.5 bg-white/20 rounded">bearbeitet</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {variant.status === 'posted' && <span className="text-xs px-2 py-0.5 bg-green-500 rounded flex items-center gap-1"><CheckCircle size={12} /> Live</span>}
                          {variant.status === 'scheduled' && <span className="text-xs px-2 py-0.5 bg-blue-500 rounded flex items-center gap-1"><Clock size={12} /> Geplant</span>}
                          {variant.status === 'simulated' && <span className="text-xs px-2 py-0.5 bg-amber-500 rounded">Simuliert</span>}
                          {variant.status === 'failed' && <span className="text-xs px-2 py-0.5 bg-red-500 rounded">Fehler</span>}
                          <button onClick={() => setEditingVariant(isEditing ? null : variant.channel)} className="p-1 hover:bg-white/20 rounded">
                            <Edit2 size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="p-4">
                        {isEditing ? (
                          <VariantEditor variant={variant} onSave={(updates) => updateVariant(variant.channel, updates)} onCancel={() => setEditingVariant(null)} />
                        ) : (
                          <div className="space-y-2 text-sm">
                            <p className="text-orange-600 font-medium">{variant.hook}</p>
                            <p className="text-stone-600 line-clamp-3">{variant.body}</p>
                            <p className="text-purple-600">{variant.cta}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {variant.hashtags?.slice(0, 5).map(tag => (
                                <span key={tag} className="text-xs text-blue-500">{tag}</span>
                              ))}
                              {variant.hashtags?.length > 5 && (
                                <span className="text-xs text-stone-400">+{variant.hashtags.length - 5}</span>
                              )}
                            </div>
                          </div>
                        )}
                        {variant.error_message && <p className="text-xs text-red-500 mt-2">{variant.error_message}</p>}
                        {variant.post_url && (
                          <a href={variant.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-2">
                            <ExternalLink size={12} /> Zum Post
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <BrandVisualHints hints={brandVisualHints} />

          {imagePromptHint && (
            <ImagePromptHintBox hint={imagePromptHint} />
          )}

          {masterpost && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Send size={20} className="text-green-600" />
                Posting-Steuerung
              </h2>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={postImmediately} onChange={() => setPostImmediately(true)} className="text-fox-fire" />
                    <span className="text-sm font-medium">Sofort posten</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={!postImmediately} onChange={() => setPostImmediately(false)} className="text-fox-fire" />
                    <span className="text-sm font-medium">Einplanen</span>
                  </label>
                </div>

                {!postImmediately && (
                  <div className="flex gap-3">
                    <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="flex-1 p-2 border border-stone-200 rounded-lg" />
                    <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-32 p-2 border border-stone-200 rounded-lg" />
                  </div>
                )}

                <div className="p-3 bg-stone-50 rounded-lg text-sm">
                  <p className="font-medium mb-1">Posting-Übersicht:</p>
                  <ul className="text-stone-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full" />
                      <span>Live: {liveStatus?.live_channels?.filter(c => selectedChannels.includes(c)).length || 0} Kanäle</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full" />
                      <span>Simuliert: {liveStatus?.simulated_channels?.filter(c => selectedChannels.includes(c)).length || 0} Kanäle</span>
                    </li>
                  </ul>
                </div>

                <button onClick={postMasterpost} disabled={posting} className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all">
                  {posting ? (
                    <><Loader2 className="animate-spin" size={18} /> Wird gepostet...</>
                  ) : postImmediately ? (
                    <><Send size={18} /> Jetzt auf {selectedChannels.length} Kanäle posten</>
                  ) : (
                    <><Clock size={18} /> Für {scheduledDate || '...'} einplanen</>
                  )}
                </button>
              </div>
            </div>
          )}

          {!masterpost && (
            <div className="bg-stone-50 rounded-xl border-2 border-dashed border-stone-300 p-8 text-center">
              <FileEdit size={48} className="mx-auto text-stone-400 mb-4" />
              <h3 className="font-heading text-lg font-bold text-stone-700 mb-2">Kanal-Varianten</h3>
              <p className="text-stone-500 text-sm">
                Fülle das Formular aus und klicke auf "Masterpost + Varianten erstellen".<br />
                Die KI generiert automatisch optimierte Versionen für jeden Kanal.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterpostPage;
